import type { CapacityContributionLocationType as LocationType, WorkingHoursOverviewRecord } from '@navio/nsdk';
import { CapacityContributionLocationType, CapacityContributionType } from '@navio/nsdk';
import { distinctInitials } from '../string/distinctInitials';
import { extractUserInitialsFromName } from '../string/string';
import {
  addToCells,
  filterCellShifts,
  groupByWorkerShift,
  type WorkingHoursCell,
  type WorkingHoursCellWorkerShift
} from './workingHoursCells';
import { groupNotBookableByWorker, type WorkingHoursNotBookableCell } from './workingHoursNotBookable';
import { coveredSeconds } from './workingHoursShiftLabels';
import {
  SHOW_OPTION_BY_LOCATION_TYPE,
  showsOnChannelLine,
  workerShiftsForShow,
  type WorkingHoursShiftShow,
  type WorkingHoursShowOption
} from './workingHoursShow';

/** A worker's bookable time on one side: the cells that side's figures count. */
export type WorkingHoursWorkerSide = {
  locationType: LocationType;
  /** Days with bookable time on this side inside the range. */
  daysOnRota: number;
  bookableSeconds: number;
  /** Clock length of the worker shifts behind that bookable time: hours on shift, breaks included. */
  shiftSeconds: number;
  cellsByDate: Map<string, WorkingHoursCell>;
};

/**
 * One worker on the board: their bookable time per side and their shifts with none. A worker
 * on both sides is still one row, so no list has to join a worker's pieces back together, and the
 * board draws it as one line (`workerDayShifts`).
 */
export type WorkingHoursWorkerRow = {
  serviceWorkerId: number;
  /** Distinct contracts behind every shift in the row, bookable or not, in first-seen order. */
  serviceWorkerContractIds: number[];
  name: string;
  /** Searched as well as the name; not shown. */
  nickname: string;
  /** For an avatar without the name beside it; no two names on the board share them (`distinctInitials`). */
  initials: string;
  /** Mobile first. Empty when none of the worker's shifts in the range has bookable time. */
  sides: WorkingHoursWorkerSide[];
  /** Days with bookable time on any side. */
  daysOnRota: number;
  bookableSeconds: number;
  /** Hours on shift across every worker shift in the row, bookable or not, each shift once. */
  shiftSeconds: number;
  /** Shifts with no bookable time, by date. They count in no side's figures. */
  notBookableCellsByDate: Map<string, WorkingHoursNotBookableCell>;
  /** Every worker shift in the row, bookable or not, as the Show filter reads it. */
  workerShiftsById: Map<number, WorkingHoursShiftShow>;
};

/** Mobile first, then garage — the reading order the board is designed around. */
export const BAND_ORDER: LocationType[] = [
  CapacityContributionLocationType.MOBILE,
  CapacityContributionLocationType.STATIONARY
];

const toWorkerSide = (
  locationType: LocationType,
  cellsByDate: Map<string, WorkingHoursCell>
): WorkingHoursWorkerSide => ({
  locationType,
  daysOnRota: cellsByDate.size,
  bookableSeconds: [...cellsByDate.values()].reduce((sum, cell) => sum + cell.bookableSeconds, 0),
  // Covered time, so one worker shift holding bookable time on both sides counts once.
  shiftSeconds: coveredSeconds([...cellsByDate.values()].flatMap((cell) => cell.shiftIntervals)),
  cellsByDate
});

/**
 * A row's hours on shift and bookable time across the given sides and shifts without bookable
 * time. One person, one clock: time on both sides at once counts once, and so does a shift.
 * With `dates`, only those days count — the board hides past days without refetching.
 */
export const workerRowTotals = (
  {
    sides,
    notBookableCellsByDate
  }: {
    sides: WorkingHoursWorkerSide[];
    notBookableCellsByDate: Map<string, WorkingHoursNotBookableCell> | undefined;
  },
  dates?: string[]
) => {
  const bookableCells = sides.flatMap((side) => cellsOnDates(side.cellsByDate, dates));
  return {
    bookableSeconds: coveredSeconds(bookableCells.flatMap((cell) => cell.intervals)),
    shiftSeconds: coveredSeconds([
      ...bookableCells.flatMap((cell) => cell.shiftIntervals),
      ...cellsOnDates(notBookableCellsByDate, dates).flatMap((cell) => cell.intervals)
    ])
  };
};

/** A row's cells, on `dates` when given. */
export const cellsOnDates = <Cell>(cellsByDate: Map<string, Cell> | undefined, dates?: string[]): Cell[] =>
  dates === undefined
    ? [...(cellsByDate?.values() ?? [])]
    : dates.flatMap((date) => {
        const cell = cellsByDate?.get(date);
        return cell === undefined ? [] : [cell];
      });

/** Every named worker with a shift in the range — bookable or not — as one row each, sorted by name. */
export const buildWorkerRows = (
  record: WorkingHoursOverviewRecord,
  workerName: (serviceWorkerId: number) => { name: string; nickname: string }
): WorkingHoursWorkerRow[] => {
  const bookableCellsByWorker = new Map<number, Map<LocationType, Map<string, WorkingHoursCell>>>();
  const contractIdsByWorker = new Map<number, Set<number>>();
  for (const shift of record.shifts) {
    if (shift.contributionType === CapacityContributionType.BASELINE || shift.serviceWorkerId == null) {
      continue;
    }
    const bySide =
      bookableCellsByWorker.get(shift.serviceWorkerId) ?? new Map<LocationType, Map<string, WorkingHoursCell>>();
    bySide.set(
      shift.locationType,
      addToCells(bySide.get(shift.locationType) ?? new Map<string, WorkingHoursCell>(), shift)
    );
    bookableCellsByWorker.set(shift.serviceWorkerId, bySide);
    if (shift.serviceWorkerContractId != null) {
      const contractIds = contractIdsByWorker.get(shift.serviceWorkerId) ?? new Set<number>();
      contractIdsByWorker.set(shift.serviceWorkerId, contractIds.add(shift.serviceWorkerContractId));
    }
  }

  const notBookableByWorker = groupNotBookableByWorker(record.shiftsWithoutBookableCapacity);
  const shiftsForShowByWorker = workerShiftsForShow(record);
  const workerIds = [...new Set([...bookableCellsByWorker.keys(), ...notBookableByWorker.keys()])];
  const initialsByName = distinctInitials(workerIds.map((serviceWorkerId) => workerName(serviceWorkerId).name));
  return workerIds
    .map((serviceWorkerId) => {
      const { name, nickname } = workerName(serviceWorkerId);
      const bySide = bookableCellsByWorker.get(serviceWorkerId);
      const sides = BAND_ORDER.flatMap((locationType) => {
        const cellsByDate = bySide?.get(locationType);
        return cellsByDate ? [toWorkerSide(locationType, cellsByDate)] : [];
      });
      const notBookable = notBookableByWorker.get(serviceWorkerId);
      return {
        serviceWorkerId,
        serviceWorkerContractIds: [
          ...new Set([
            ...(contractIdsByWorker.get(serviceWorkerId) ?? []),
            ...(notBookable?.serviceWorkerContractIds ?? [])
          ])
        ],
        name,
        nickname,
        initials: initialsByName.get(name) ?? extractUserInitialsFromName(name),
        sides,
        daysOnRota: new Set(sides.flatMap((side) => [...side.cellsByDate.keys()])).size,
        ...workerRowTotals({ sides, notBookableCellsByDate: notBookable?.cellsByDate }),
        notBookableCellsByDate: notBookable?.cellsByDate ?? new Map<string, WorkingHoursNotBookableCell>(),
        workerShiftsById: shiftsForShowByWorker.get(serviceWorkerId) ?? new Map<number, WorkingHoursShiftShow>()
      };
    })
    .sort((left, right) => left.name.localeCompare(right.name));
};

/** Live name filter for the board's worker search, on the name and the nickname. Empty query matches everyone. */
const workerMatchesSearch = (row: Pick<WorkingHoursWorkerRow, 'name' | 'nickname'>, search: string) => {
  const query = search.trim().toLowerCase();
  return query.length === 0 || [row.name, row.nickname].some((value) => value.toLowerCase().includes(query));
};

/**
 * A side with only the worker shifts the Show filter keeps on its line, its figures counted again
 * from them. The same side when nothing is dropped; none when nothing is left.
 */
const sideOnShow = (
  row: WorkingHoursWorkerRow,
  side: WorkingHoursWorkerSide,
  show: WorkingHoursShowOption[]
): WorkingHoursWorkerSide[] => {
  const cells = [...side.cellsByDate.values()].flatMap((cell) => {
    const kept = filterCellShifts(cell, (shift) =>
      showsOnChannelLine(
        // Bookable time outside a worker shift is just bookable time, on its own channel.
        (shift.workerShiftId === null ? undefined : row.workerShiftsById.get(shift.workerShiftId)?.options) ?? [
          SHOW_OPTION_BY_LOCATION_TYPE[side.locationType]
        ],
        side.locationType,
        show
      )
    );
    return kept ? [kept] : [];
  });
  if (cells.length === 0) {
    return [];
  }
  const isUnchanged =
    cells.length === side.cellsByDate.size && cells.every((cell) => side.cellsByDate.get(cell.date) === cell);
  return [isUnchanged ? side : toWorkerSide(side.locationType, new Map(cells.map((cell) => [cell.date, cell])))];
};

/**
 * The rows a board lists for the department's sides, the Show filter and the name search. Each
 * row keeps only the shifts on show (its totals still count every shift), and a row left with
 * nothing to draw is dropped. A shift shows on its channel's line for that channel, or for Not
 * fully bookable when part of it is not; a shift with no bookable time shows for that only.
 */
export const visibleWorkerRows = (
  rows: WorkingHoursWorkerRow[],
  {
    locationTypes,
    show,
    search = ''
  }: { locationTypes: LocationType[]; show: WorkingHoursShowOption[]; search?: string }
): WorkingHoursWorkerRow[] =>
  rows
    .filter((row) => workerMatchesSearch(row, search))
    .map((row) => ({
      ...row,
      sides: row.sides.flatMap((side) =>
        locationTypes.includes(side.locationType) ? sideOnShow(row, side, show) : []
      ),
      notBookableCellsByDate: show.includes('not-bookable')
        ? row.notBookableCellsByDate
        : new Map<string, WorkingHoursNotBookableCell>()
    }))
    .filter((row) => row.sides.length > 0 || row.notBookableCellsByDate.size > 0);

/**
 * A row's worker shifts with bookable time on one date, each once and in the order they start: a
 * shift bookable on both channels is one entry naming both.
 */
export const workerDayShifts = (row: WorkingHoursWorkerRow, date: string): WorkingHoursCellWorkerShift[] =>
  groupByWorkerShift(
    row.sides.flatMap(
      (side) => side.cellsByDate.get(date)?.shifts.map((shift) => ({ shift, locationType: side.locationType })) ?? []
    ),
    BAND_ORDER
  );

/** The worker shifts a row draws on one date, bookable or not, each once. */
export const workerShiftsOnDate = (row: WorkingHoursWorkerRow, date: string): WorkingHoursShiftShow[] => {
  const workerShiftIds = new Set([
    ...row.sides.flatMap((side) => side.cellsByDate.get(date)?.shifts.map((shift) => shift.workerShiftId) ?? []),
    ...(row.notBookableCellsByDate.get(date)?.shifts.map((shift) => shift.workerShiftId) ?? [])
  ]);
  return [...workerShiftIds].flatMap((workerShiftId) => {
    const shiftShow = workerShiftId === null ? undefined : row.workerShiftsById.get(workerShiftId);
    return shiftShow ? [shiftShow] : [];
  });
};

/** Each worker's bookable cell on one side and date, in board order. */
export const workerCellsOnDate = (rows: WorkingHoursWorkerRow[], locationType: LocationType, date: string) =>
  rows.flatMap((row) => {
    const cell = row.sides.find((side) => side.locationType === locationType)?.cellsByDate.get(date);
    return cell ? [{ row, cell }] : [];
  });
